import type { ReactNode } from "react";
import { Fragment, useState } from "react";
import { Link, useParams } from "react-router-dom";

import type { Sort } from "../api/queries";
import { useScanDetail, useScanOffers, useScanPackages } from "../api/queries";
import type { ScanSummary } from "../api/types";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { LotsTable } from "../components/LotsTable";
import { ModePill } from "../components/ModePill";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { Variation, VariantPrices } from "../components/Variation";
import { adena, duration, offerPrice, signed, when } from "../format";
import { itemHref, traderHref } from "../search/nav";
import { displayModes } from "./scanRoute";

const SORTS: Sort[] = ["item", "spread", "buy", "sell"];

/** Null means there was no earlier scan from this source and city to compare
 *  against — dim, and printed as an em dash. Everything else takes the table's
 *  own text colour: the sign already carries direction, and rose and olive are
 *  spoken for as WTS and WTB in the very same row. DESIGN.md fixes that budget
 *  and specifies no delta treatment, so inventing one was the mistake. */
function deltaClass(delta: number | null): string {
  return delta === null ? "dim" : "";
}

/** The line under the title: which scan this is, then the facts the scans table
 *  used to carry in its own columns. This page is only ever one named scan now —
 *  "last scan" was only ever true of the bare path, and the bare path has moved
 *  to Market.tsx, which shows the whole history instead. */
function ScanMeta({ scan }: { scan: ScanSummary }) {
  const parts: ReactNode[] = [`scan ${when(scan.started_at)}`];
  if (scan.modes.length > 0) parts.push(displayModes(scan.modes).join(", "));
  if (scan.city) parts.push(scan.city);
  if (scan.source) parts.push(scan.source);
  if (scan.presumed_stopped) {
    parts.push(
      <span title="Started long ago and never closed — the machine probably lost power.">
        stopped
      </span>,
    );
  } else if (scan.ended_at !== null) {
    parts.push(`took ${duration(scan.started_at, scan.ended_at)}`);
  }

  return (
    <p className="scan-meta">
      {parts.map((part, index) => (
        <Fragment key={index}>
          {/* The dot is drawn, not read: "Giran dot windows-box" is worse aloud
              than the two facts on their own. */}
          {index > 0 ? <span aria-hidden="true">·</span> : null}
          <span>{part}</span>
        </Fragment>
      ))}
    </p>
  );
}

/** The items of one named scan, which is what this application is for. The
 *  scan list itself lives at /scans, reached from the header nav. */
export function Items() {
  const { uid = "" } = useParams();
  const [sort, setSort] = useState<Sort>("item");
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const detail = useScanDetail(uid, sort);
  const offers = useScanOffers(uid, selected);
  const packages = useScanPackages(uid);

  if (detail.isLoading) return <><TitleBand title="Items" /><Skeleton rows={8} /></>;
  if (detail.isError) {
    return (
      <>
        <TitleBand title="Items" />
        <ErrorBand message="Could not load this scan." hint="It may have been removed, or the link is wrong." />
      </>
    );
  }

  // react-query pauses a query when the browser is offline (the default
  // networkMode), and a paused query has isLoading false with data undefined.
  // Destructuring that threw; Scans and Item already take the safe route.
  if (!detail.data) {
    return (
      <>
        <TitleBand title="Items" />
        <Skeleton rows={8} />
      </>
    );
  }
  const { scan, best } = detail.data;
  const needle = filter.trim().toLowerCase();
  const rows = needle ? best.filter((r) => r.item.toLowerCase().includes(needle)) : best;
  // Undefined while the packages query is in flight or paused — its own panel
  // simply is not there yet, which is right: it is a footnote to the scan, not
  // something worth a second skeleton above the table.
  const lots = packages.data ?? [];

  return (
    <>
      <TitleBand
        title="Items"
        note={`${scan.offers_count} offers from ${scan.traders_count} traders`}
      />
      <ScanMeta scan={scan} />
      <Panel primary>
        <div className="strip">
          <span>Sort</span>
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => setSort(option)}
            >
              {option}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <input
            aria-label="Filter items"
            placeholder="filter item…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {rows.length === 0 ? (
          <Empty>{needle ? `Nothing matching “${filter}”.` : "This scan recorded no offers."}</Empty>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Best buy</th>
                  <th className="num">#</th>
                  <th className="num">Best sell</th>
                  <th className="num">#</th>
                  <th className="num">Spread</th>
                  <th className="num">Buy vs last</th>
                  <th className="num">Sell vs last</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.item_id}
                    className={row.item === selected ? "sel" : undefined}
                    onClick={() => setSelected(row.item === selected ? null : row.item)}
                  >
                    <td>
                      <Link
                        to={itemHref(row.item_id, row.item)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.item}
                      </Link>
                      {/* Nobody listed this one plain in this walk, so every
                          figure across the row is a variant's. Unmarked, BEST
                          BUY reads as what the plain item fetches — see
                          queries.best_prices, which would rather show the row
                          than drop the item off its own scan's table. */}
                      <VariantPrices from={row.from_variants} />
                    </td>
                    <td className="num buy">{adena(row.best_buy)}</td>
                    <td className="num dim">{row.buyers || ""}</td>
                    <td className="num sell">{adena(row.best_sell)}</td>
                    <td className="num dim">{row.sellers || ""}</td>
                    <td className="num">{signed(row.spread)}</td>
                    <td
                      className={`num ${deltaClass(row.delta_buy)}`}
                      title="Against the previous scan from the same machine and city"
                    >
                      {signed(row.delta_buy)}
                    </td>
                    <td
                      className={`num ${deltaClass(row.delta_sell)}`}
                      title="Against the previous scan from the same machine and city"
                    >
                      {signed(row.delta_sell)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* A package is one price for a set of items, so it gets its own table
          rather than a place in the one above: its rows have no per-item price
          to sort or compare, and the lot is only legible whole. Silent when the
          scan walked no packages — most do not. */}
      {lots.length > 0 ? (
        <>
          <TitleBand
            title="Package sales"
            note={`${lots.length} ${lots.length === 1 ? "lot" : "lots"}, sold whole`}
          />
          <Panel>
            <LotsTable lots={lots} />
          </Panel>
        </>
      ) : null}

      {selected ? (
        <>
          <TitleBand title={selected} note="most recent 200 in this scan" />
          <Panel>
            {offers.isLoading ? (
              <Skeleton rows={3} />
            ) : offers.isError ? (
              // Without this the table renders its headers over zero rows, which
              // reads as "this item had no offers" — a state that cannot occur,
              // since every row in the table above was derived from at least one.
              <ErrorBand
                message="Could not load this item's offers."
                hint="The scan itself loaded fine. Reload in a moment."
              />
            ) : (
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Mode</th>
                      <th>Trader</th>
                      <th className="num">Qty</th>
                      <th className="num">Price</th>
                      <th>Via</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(offers.data?.offers ?? []).map((offer) => (
                      <tr key={offer.id}>
                        <td><ModePill mode={offer.mode} /></td>
                        <td>
                          <Link to={traderHref(offer.nick)}>{offer.nick}</Link>
                          {/* The panel is already one item; this says which of
                              it they had. */}
                          <Variation {...offer} />
                        </td>
                        <td className="num">{adena(offer.qty)}</td>
                        <td className={`num ${offer.mode}`}>{offerPrice(offer)}</td>
                        <td className="dim">{offer.via_item}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      ) : null}
    </>
  );
}
